<template>
  <v-card tile>
    <v-card-title class="font-weight-regular">
      {{ $t('PERSONS.MAIN_INFORMATION') }}

      <v-icon
        v-if="!editMode"
        class="ml-2"
        color="indigo"
        @click="toggleEditMode"
        >mdi-square-edit-outline</v-icon
      >

      <v-spacer></v-spacer>
      <div>
        <span class="text-body-2"> {{ $t('PERSONS.STATUS') }}:</span>
      </div>
      <div>
        <v-select
          v-model="form.status_id"
          solo
          flat
          :items="statuses"
          item-value="id"
          item-text="name"
          small-chips
          hide-details
          dense
          :disabled="!editMode"
          style="width: 130px"
        >
          <template v-slot:selection="{ attrs, item, select, selected }">
            <v-chip
              small
              :color="item.color"
              dark
              class="ml-1"
              v-bind="attrs"
              :input-value="selected"
              @click="select"
            >
              {{ item.name }}
            </v-chip>
          </template>
        </v-select>
      </div>
    </v-card-title>
    <v-divider />
    <v-card-text>
      <v-form ref="mainInformationForm" v-model="valid" class="px-4">
        <v-row>
          <v-col>
            <person-form
              v-model="form"
              :readonly="!editMode"
              :errors="errors.get('person')"
              @update-prop="clearErrorByName($event, 'person')"
            />

            <personal-profile-form
              v-model="form.personal_profile"
              :readonly="!editMode"
              :errors="errors.get('personal_profile')"
              @update-prop="clearErrorByName($event, 'personal_profile')"
            />

            <v-card min-height="142" elevation="0" class="mt-2">
              <v-card-text class="pa-0">
                <main-person-tabs-form
                  v-model="form"
                  :readonly="!editMode"
                  :errors="errors"
                  @update-prop="clearErrorByName($event)"
                />
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="pt-0">
            <div v-if="editMode">
              <v-btn
                outlined
                class="mr-5"
                depressed
                color="grey"
                large
                @click="cancel"
              >
                {{ $t('BUTTON.CANCEL') }}
              </v-btn>
              <v-btn
                depressed
                type="submit"
                :disabled="!valid"
                large
                color="primary"
                class="mr-5"
                @click.prevent="updatePerson"
              >
                {{ $t('BUTTON.UPDATE') }}
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapActions } from 'vuex';
import { mapCacheActions } from 'vuex-cache';
import PersonForm from '@/modules/persons/components/forms/PersonForm';
import PersonalProfileForm from '@/modules/persons/components/forms/PersonalProfileForm';
import MainPersonTabsForm from '@/modules/persons/components/forms/MainPersonTabsForm';
import PersonInvoicesEmail from '@/modules/persons/components/mixins/person-invoices-email.js';
import errors from '@/modules/_global/mixins/errors';
import { cloneDeep, isEqual } from 'lodash';

export default {
  name: 'MainInformation',

  components: {
    PersonForm,
    MainPersonTabsForm,
    PersonalProfileForm
  },

  mixins: [errors, PersonInvoicesEmail],

  props: {
    value: {
      type: Object,
      default: () => ({})
    }
  },

  data: () => ({
    form: {},
    valid: false,
    editMode: false,
    formCopy: null,
    statuses: []
  }),

  watch: {
    editMode: {
      deep: true,
      immediate: true,
      handler: function(v) {
        if (v) {
          this.formCopy = cloneDeep(this.form);
        }
      }
    },

    value: {
      deep: true,
      immediate: true,
      handler: function(formProps) {
        if (isEqual(formProps, this.form)) {
          return;
        }

        this.form = {
          ...this.form,
          ...formProps
        };

        this.emitter();
      }
    }
  },

  created() {
    this.getStatuses().then(({ data }) => {
      this.statuses = data.data;
    });
  },

  methods: {
    ...mapCacheActions('person', ['getStatuses']),
    ...mapActions('person', ['update']),

    emitter() {
      this.$emit('input', this.form);
    },

    toggleEditMode() {
      this.editMode = !this.editMode;
    },

    cancel() {
      this.form = cloneDeep(this.formCopy);
      this.toggleEditMode();
    },

    updatePerson() {
      let { personal_profile = null, ...person } = this.form;

      this.update({
        person: person,
        personal_profile
      })
        .then(response => {
          this.$emit('update', response.data);
          this.toggleEditMode();
        })
        .catch(({ response }) => {
          this.handleErrorsByResponse(response);
        });
    }
  }
};
</script>

<style scoped></style>
